import { mergeAttributes, Node, type Attribute, type JSONContent } from "@tiptap/core"
import type { Node as PMNode } from "@tiptap/pm/model"
import { NodeSelection } from "@tiptap/pm/state"
import { shouldIncludeAttributes } from "../../util/markdown-options"
import { mdLink } from "../../util/markdown-template"
import {
  escapeXml,
  formatXmlAttributes,
  parseXmlAttributes,
  unescapeXml,
  XML_ATTRIBUTES_SOURCE,
} from "../../util/markdown-xml-attributes"
import { toTiptapAttributes } from "../../util/render-data"
import {
  BLOCK_RENDER_AS,
  INLINE_RENDER_AS,
  LinkAttrsSchema,
  linkFromUrl,
  LinkMarkdownAttrsSchema,
  type Link,
  type LinkRenderAs,
} from "./link-attrs"

// The text is escaped on render, so the first "</a>" is this tag's.
const LINK_TAG = new RegExp(`^<a(${XML_ATTRIBUTES_SOURCE})\\s*>([\\s\\S]*?)</a>`, "i")

/** Whether `node` is a unified link token (either form). */
export function isLinkNode(node: PMNode): boolean {
  return node.type.name === "linkInline" || node.type.name === "linkBlock"
}

/**
 * Shared addAttributes for both link node types: one plain-scalar `data-*` entry per
 * Link field, with `renderAs` clamped to the node type's `allowed` subset (its first
 * entry is the default) so an out-of-subset value can't be constructed, parsed, or
 * stored.
 */
function linkAttributes(allowed: readonly LinkRenderAs[]): Record<keyof Link, Partial<Attribute>> {
  const clampRenderAs = (value: unknown): LinkRenderAs =>
    allowed.includes(value as LinkRenderAs) ? (value as LinkRenderAs) : allowed[0]

  return toTiptapAttributes(LinkAttrsSchema, {
    url: {
      parseHTML: (element) => element.getAttribute("href"),
      renderHTML: (attributes) => ({ href: attributes.url }),
    },
    renderAs: {
      default: allowed[0],
      renderHTML: (attributes) => ({ "data-render-as": clampRenderAs(attributes.renderAs) }),
      parseHTML: (element) => clampRenderAs(element.getAttribute("data-render-as")),
    },
  })
}

export const LinkInlineContentExtension = Node.create({
  name: "linkInline",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes: () => linkAttributes(INLINE_RENDER_AS),

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as Link
    return [
      "a",
      mergeAttributes(HTMLAttributes, { "data-content-type": "linkInline", href: attrs.url }),
      attrs.title ?? attrs.url,
    ]
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-content-type="linkInline"]',
        priority: 60,
        getAttrs: (element) => ({
          title: element.getAttribute("data-title") || element.textContent || null,
        }),
      },
      {
        tag: "a[href]",
        priority: 52,
        getAttrs: (element) => {
          if (element.hasAttribute("data-content-type")) return false
          const href = element.getAttribute("href")
          if (!href) return false
          return linkFromUrl(href, element.textContent || href)
        },
      },
    ]
  },

  markdownTokenName: "link",
  // Plain `[text](url)` links are left to marked's own link tokenizer.
  markdownTokenizer: {
    name: "link",
    level: "inline",
    start: (src) => src.search(/<a[\s>]/i),
    tokenize(src) {
      const match = LINK_TAG.exec(src)
      if (!match) return
      const attributes = parseXmlAttributes(match[1], LinkMarkdownAttrsSchema)
      if (!attributes) return
      const { href, ...rest } = attributes
      return { type: "link", raw: match[0], href, text: unescapeXml(match[2]), attributes: rest }
    },
  },
  renderMarkdown: renderLinkMarkdown,
  parseMarkdown: (token) => ({
    type: "linkInline",
    attrs: parseMarkdownLinkAttrs(token.attributes ?? {}, token.href, token.text || token.href),
  }),

  // Defined once here for both node types (the command registry is editor-global).
  addCommands() {
    return {
      setLinkRenderAs:
        (renderAs: LinkRenderAs) =>
        ({ state, tr, dispatch }) => {
          const { selection } = state
          if (!(selection instanceof NodeSelection) || !isLinkNode(selection.node)) return false
          const node = selection.node
          const attrs = { ...(node.attrs as Link), renderAs }
          const targetTypeName = (INLINE_RENDER_AS as readonly string[]).includes(renderAs)
            ? "linkInline"
            : "linkBlock"

          if (node.type.name === targetTypeName) {
            if (dispatch && attrs.renderAs !== node.attrs.renderAs) {
              tr.setNodeMarkup(selection.from, undefined, attrs)
            }
            return true
          }

          if (dispatch) {
            const token = state.schema.nodes[targetTypeName].create(attrs)
            let tokenPos: number
            if (targetTypeName === "linkBlock") {
              const $from = state.doc.resolve(selection.from)
              if ($from.parent.childCount === 1) {
                // The token is its paragraph's only child: the paragraph becomes the block.
                tokenPos = $from.before()
                tr.replaceWith($from.before(), $from.after(), token)
              } else {
                // Pull the token out of its paragraph, onto its own row right after it.
                const insertPos = $from.after()
                tr.delete(selection.from, selection.to)
                tokenPos = tr.mapping.map(insertPos)
                tr.insert(tokenPos, token)
              }
            } else {
              // block → inline: wrap in a paragraph in place.
              tokenPos = selection.from + 1
              tr.replaceWith(
                selection.from,
                selection.to,
                state.schema.nodes.paragraph.create(null, token),
              )
            }
            // Keep the token selected so the menu/dialog keep operating on it.
            tr.setSelection(NodeSelection.create(tr.doc, tokenPos))
          }
          return true
        },
    }
  },
})

export const LinkBlockContentExtension = Node.create({
  name: "linkBlock",
  group: "block widget",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes: () => linkAttributes(BLOCK_RENDER_AS),

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as Link
    return [
      "a",
      mergeAttributes(HTMLAttributes, { "data-content-type": "linkBlock", href: attrs.url }),
      attrs.title ?? attrs.url,
    ]
  },

  parseHTML() {
    return [{ tag: 'a[data-content-type="linkBlock"]', priority: 60 }]
  },

  renderMarkdown: renderLinkMarkdown,
  // Block links are parsed as linkInline, then converted to linkBlock in card-stack-markdown.ts.
})

function renderLinkMarkdown(node: JSONContent): string {
  const attrs = LinkAttrsSchema.parse(canonicalLinkAttrs(node))
  const text = attrs.title || attrs.url
  const attributes = shouldIncludeAttributes()
    ? formatXmlAttributes({
        ...attrs,
        url: undefined,
        title: undefined,
        type: attrs.type === "link" ? undefined : attrs.type,
        source: attrs.source === "link" ? undefined : attrs.source,
        renderAs: attrs.renderAs === "hyperlink" ? undefined : attrs.renderAs,
      })
    : ""
  // Without attributes, a plain Markdown link says the same thing.
  if (!attributes) return mdLink(text, attrs.url)
  return `<a${formatXmlAttributes({ href: attrs.url })}${attributes}>${escapeXml(text)}</a>`
}

function canonicalLinkAttrs(node: JSONContent) {
  const raw = node.attrs ?? {}
  const url = raw.url || ""
  const { url: _url, ...attributes } = raw
  const parsed = parseMarkdownLinkAttrs(attributes, url, raw.title || url)
  const allowed = node.type === "linkBlock" ? BLOCK_RENDER_AS : INLINE_RENDER_AS

  return {
    ...parsed,
    renderAs: allowed.find((value) => value === parsed.renderAs) ?? allowed[0],
  }
}

function parseMarkdownLinkAttrs(
  attributes: Record<string, unknown>,
  url: string,
  title: string | null,
) {
  const parsed = LinkAttrsSchema.safeParse({ ...attributes, url })
  const data = parsed.success ? parsed.data : LinkAttrsSchema.parse({ url })
  return {
    ...data,
    title: (attributes.title as string) || title,
  }
}
